/**
 * @tagadapay/builder-protocol
 * 
 * Protocol for embedding external builders into the Tagada CRM funnel editor.
 * Communication happens via postMessage between the CRM (host) and builder (iframe).
 * 
 * The CRM is the host — it owns auth, resources, deploy, and navigation.
 * The builder is the client — it owns the editing UI (canvas, inspector, etc.).
 * 
 * This is similar to Shopify's App Bridge pattern.
 */

// ─── Protocol Version ────────────────────────────────────────────────

export const PROTOCOL_VERSION = '1' as const

/** All messages are prefixed with this namespace to avoid collisions */
export const MESSAGE_PREFIX = 'tagada' as const

// ─── Step Types ──────────────────────────────────────────────────────

export type StepType = 
  | 'landing' 
  | 'checkout' 
  | 'offer' 
  | 'thankyou' 
  | 'custom' 
  | 'error'
  | 'external'

// ─── Node Types (extensible in the future) ───────────────────────────

export type NodeEditorType = 
  | 'page'      // Visual page editor (Studio)
  | 'email'     // Email flow editor  
  | 'event'     // Server-side event configurator
  | 'webhook'   // Webhook configurator
  | 'custom'    // Third-party custom node

// ─── Host → Builder Messages ─────────────────────────────────────────

/** 
 * Initialize the builder with context about the current store, funnel, and step.
 * Sent once when the iframe loads. 
 */
export interface InitMessage {
  type: `${typeof MESSAGE_PREFIX}:init`
  version: typeof PROTOCOL_VERSION
  payload: {
    /** Store ID */
    storeId: string
    /** Funnel ID (if editing within a funnel) */
    funnelId?: string
    /** Step ID being edited */
    stepId?: string
    /** Type of step (landing, checkout, offer, thankyou, custom) */
    stepType?: StepType
    /** Session token for API authentication */
    sessionToken: string
    /** Host capabilities — builder can check before requesting features */
    capabilities: HostCapability[]
    /** 
     * Runtime step configuration from CRM (order bumps, payment methods, upsells, etc.).
     * Opaque JSON — the builder can use it to hydrate commerce blocks at design time.
     */
    stepConfig?: Record<string, unknown> | null
    /** UI preferences */
    ui?: {
      theme?: 'light' | 'dark'
      locale?: string
    }
  }
}

/** Host capabilities that the builder can check for */
export type HostCapability = 
  | 'preview'         // Host can create preview sessions
  | 'resources'       // Host can provide store resources (products, offers)
  | 'deploy'          // Host handles deploy pipeline
  | 'analytics'       // Host can provide step analytics

/**
 * Load an existing page/content for editing.
 * Sent after init, when there's existing content to edit.
 *
 * `pluginId` and `deploymentId` are required for `mode: 'edit'` and
 * `mode: 'new-page'` but optional for `mode: 'create'` — in create mode the
 * builder is constructing a new plugin/deployment from scratch (optionally
 * pre-seeded by `sourceData`) and the resulting IDs are returned via the
 * SaveMessage callback.
 */
export interface LoadMessage {
  type: `${typeof MESSAGE_PREFIX}:load`
  payload: {
    /** Plugin ID (required for edit/new-page modes; omitted in create mode) */
    pluginId?: string
    /** Deployment ID (required for edit/new-page modes; omitted in create mode) */
    deploymentId?: string
    /** Page ID or path to edit */
    pageId?: string
    /**
     * Source data for round-trip editing.
     * Opaque to the host — only the builder understands this format.
     */
    sourceData?: unknown
    /**
     * The page the host already read with the builder session, from the
     * builder's own source-data endpoint. A builder that knows this field opens
     * it as it opens its own read; an older one ignores it and reads the page
     * itself. Opaque to the host.
     */
    hostSourceData?: unknown
    /** Edit mode */
    mode: 'edit' | 'new-page' | 'create'
    /**
     * Suggested name for a new page (used in create mode).
     * Helps generate a unique pluginId so pages don't overwrite each other.
     */
    suggestedName?: string
  }
}

/**
 * Response to builder's preview session request.
 */
export interface PreviewSessionMessage {
  type: `${typeof MESSAGE_PREFIX}:preview-session`
  payload: {
    /** CMS auth token */
    token: string
    /** Checkout token (for checkout pages) */
    checkoutToken?: string
    /** Funnel session ID */
    funnelSessionId: string
    /** Full preview URL */
    previewUrl: string
    /** Additional resources */
    resources?: Record<string, string>
    /** Error message — if set, the session creation failed */
    error?: string
    /** Correlation ID — echoed from the request */
    _requestId?: string
  }
}

/**
 * Response to the builder's session request (#TAG-3478): a fresh builder
 * session token, minted the way the one in `init` was, with the same access
 * check. On a refusal `error` says why and the token fields are empty.
 */
export interface SessionMessage {
  type: `${typeof MESSAGE_PREFIX}:session`
  payload: {
    /** The new builder session token; empty when `error` is set */
    sessionToken: string
    /** When it ends, ISO 8601; empty when `error` is set */
    expiresAt: string
    /** Error message — if set, no token was minted */
    error?: string
    /** Correlation ID — echoed from the request */
    _requestId?: string
  }
}

/**
 * Response to builder's resources request.
 */
export interface StoreProductResource {
  id: string
  name: string
  image?: string
  variants?: Array<{
    id: string
    name: string
    image?: string
    prices?: Array<{
      id: string
      amount: number
      currency: string
      interval?: string
    }>
  }>
}

export interface StoreOrderResource {
  id: string
  orderNumber: string
  status: string
  amount: number
  currency: string
  customerEmail?: string
  createdAt?: string
}

export interface ResourcesMessage {
  type: `${typeof MESSAGE_PREFIX}:resources`
  payload: {
    products?: StoreProductResource[]
    offers?: Array<{ id: string; name: string; type?: string }>
    shippingRates?: Array<{ id: string; name: string; price: number }>
    orders?: StoreOrderResource[]
    /** Correlation ID — echoed from the request */
    _requestId?: string
  }
}

/**
 * Host requests the builder to save its current state.
 * Builder should respond with a SaveMessage.
 */
export interface RequestSaveMessage {
  type: `${typeof MESSAGE_PREFIX}:request-save`
}

/**
 * Host informs builder of a viewport/device change.
 */
export interface ViewportMessage {
  type: `${typeof MESSAGE_PREFIX}:viewport`
  payload: {
    device: 'mobile' | 'tablet' | 'desktop'
    width?: number
    height?: number
  }
}

/**
 * Host acknowledges the builder's save was processed.
 */
export interface SaveAckMessage {
  type: `${typeof MESSAGE_PREFIX}:save-ack`
  payload: {
    success: boolean
    error?: string
    /** New deployment ID after successful deploy */
    deploymentId?: string
    /** New instance ID */
    instanceId?: string
    /** Correlation ID — echoed from the request */
    _requestId?: string
  }
}

/**
 * Host pushes updated step configuration to the builder.
 * Sent when stepConfig changes in the CRM while the builder is open
 * (e.g. user toggles an order bump in the side panel).
 */
export interface StepConfigMessage {
  type: `${typeof MESSAGE_PREFIX}:step-config`
  payload: {
    stepConfig: Record<string, unknown> | null
    /** Set when this answers a `patch-step-config`, so the builder can match it. */
    _requestId?: string
    /**
     * The patch was refused and `stepConfig` is the config as it still stands.
     * The canvas refuses a command it cannot compile (a funnel that would no
     * longer validate, a stale sequence); the builder shows this and keeps the
     * config it was given rather than the value it tried to write.
     */
    refusal?: {
      code: string
      message: string
    }
  }
}

// ─── Media Library Messages ──────────────────────────────────────────

/** Asset metadata returned from the media library */
export interface MediaAsset {
  id: string
  url: string
  filename: string
  mimeType: string
  fileSize?: number
  width?: number
  height?: number
  folder?: string
  tags?: string[]
  alt?: string
  createdAt?: string
}

/** Host -> Builder: media library listing results */
export interface MediaLibraryMessage {
  type: `${typeof MESSAGE_PREFIX}:media-library`
  payload: {
    assets: MediaAsset[]
    pagination: { hasNext: boolean; total: number; page: number }
    /** Correlation ID — echoed from the request */
    _requestId?: string
  }
}

/** Host -> Builder: confirmation that an asset was registered */
export interface MediaAssetRegisteredMessage {
  type: `${typeof MESSAGE_PREFIX}:media-asset-registered`
  payload: { id: string; url: string; _requestId?: string }
}

/**
 * Host -> Builder: the host's colour scheme, so an embedded builder matches
 * the app around it instead of the operating system's preference. Resolved
 * by the host (never 'system'); sent once the builder is ready and again
 * whenever the host's theme changes. A builder that does not know it drops it.
 */
export interface ThemeMessage {
  type: `${typeof MESSAGE_PREFIX}:theme`
  payload: { theme: 'light' | 'dark' }
}

/**
 * Host sends the full funnel context (steps, edges, current step) so the
 * builder can render a funnel flow mini-map / step navigator.
 * Sent after init and updated whenever the funnel structure changes.
 */
export interface FunnelContextMessage {
  type: `${typeof MESSAGE_PREFIX}:funnel-context`
  payload: {
    funnelId: string
    funnelName: string
    steps: Array<{
      id: string
      name: string
      type: StepType | string
      isEntry?: boolean
      path?: string
      /** ID of the page being edited in this Studio session */
      isCurrentEdit?: boolean
    }>
    edges: Array<{
      source: string
      target: string
    }>
    /** Step ID currently being edited */
    currentStepId?: string
  }
}

// ─── Host assistant ↔ Builder ────────────────────────────────────────
//
// The host's assistant edits the page through the builder (#TAG-3137): it
// sends the builder's own tool calls, one at a time, and the builder runs them
// on its editor exactly as its own AI did. Both directions share one message
// type, told apart by `payload.kind`.

/** What a message to the assistant is about: the selected elements when it was sent, or the whole page. */
export type AgentScope = { ids: string[]; labels: string[]; kinds: string[] } | 'page'

/** Where a turn's changes sit in the builder's undo history. */
export type AgentTurnState = 'undoable' | 'buried' | 'undone' | 'gone'

/** One image a tool answered with: a data URL or an https URL. */
export type AgentImage = string

/**
 * Host -> Builder.
 *
 * - `snapshot`: before each request of a turn. The first one for a `turnId`
 *   opens the turn: its scope is fixed then, and its changes collect into one
 *   undo step. Answered by a `snapshot` with the same `requestId`.
 * - `call`: run one tool of the turn. Answered by a `result` with the same `callId`.
 * - `turn-end`: the turn is over (answered, stopped or failed): close its undo step.
 * - `undo-turn`: revert a turn, when it is still on top of the history.
 * - `outline`: ring these elements on the canvas (empty: clear).
 * - `select`: select these elements.
 */
export interface AgentHostMessage {
  type: `${typeof MESSAGE_PREFIX}:agent`
  payload:
    | { kind: 'snapshot'; requestId: string; turnId: string }
    | { kind: 'call'; turnId: string; callId: string; tool: string; args: Record<string, unknown> }
    | { kind: 'turn-end'; turnId: string }
    | { kind: 'undo-turn'; turnId: string }
    | { kind: 'outline'; ids: string[] }
    | { kind: 'select'; ids: string[] }
}

/**
 * Builder -> Host.
 *
 * - `snapshot`: the page as the model must see it now, and the turn's scope.
 * - `result`: one tool's answer. `text` is what the model reads; `outside`
 *   says it changed an element outside the turn's scope.
 * - `selection`: the merchant's selection changed.
 * - `turns`: where each known turn sits in the undo history, after any change
 *   of it, and which elements of the known turns' scopes are no longer on the page.
 * - `ask`: a control of the builder asks the assistant for a job ("Translate
 *   with AI"): the host opens its assistant and sends `prompt`, showing `display`.
 */
export interface AgentBuilderMessage {
  type: `${typeof MESSAGE_PREFIX}:agent`
  payload:
    | { kind: 'snapshot'; requestId: string; pageState: string; scope: AgentScope }
    | { kind: 'result'; callId: string; ok: boolean; text: string; images?: AgentImage[]; outside?: boolean }
    | { kind: 'selection'; scope: AgentScope }
    | { kind: 'turns'; states: Record<string, AgentTurnState>; missing: string[] }
    | { kind: 'ask'; prompt: string; display?: string }
}

/** All messages the host can send to the builder */
export type HostMessage = 
  | InitMessage
  | LoadMessage
  | PreviewSessionMessage
  | SessionMessage
  | ResourcesMessage
  | RequestSaveMessage
  | ViewportMessage
  | SaveAckMessage
  | StepConfigMessage
  | FunnelContextMessage
  | ThemeMessage
  | AgentHostMessage
  | MediaLibraryMessage
  | MediaAssetRegisteredMessage

// ─── Builder → Host Messages ─────────────────────────────────────────

/**
 * Builder signals it's loaded and ready to receive messages.
 */
export interface ReadyMessage {
  type: `${typeof MESSAGE_PREFIX}:ready`
  payload: {
    /** Builder name (for debugging) */
    name?: string
    /** Builder version */
    version?: string
    /** What the builder supports */
    capabilities?: BuilderCapability[]
  }
}

export type BuilderCapability = 
  | 'visual-editor'    // WYSIWYG canvas
  | 'code-editor'      // Code/HTML editor
  | 'preview'          // Builder has its own preview
  | 'multi-page'       // Builder supports multiple pages
  | 'render-signal'    // Builder sends `rendered` once its first view is drawn

/**
 * Builder has unsaved changes (host can show indicator).
 */
export interface DirtyMessage {
  type: `${typeof MESSAGE_PREFIX}:dirty`
  payload: {
    isDirty: boolean
  }
}

/**
 * Builder notifies the host that it has deployed successfully.
 * 
 * The builder handles its own build + deploy via the public API
 * (POST /api/public/v1/plugins/v2/deploy). It then sends the result
 * IDs to the host so the CRM can update the step config.
 * 
 * The host does NOT receive raw assets — the builder deploys itself.
 * This keeps the protocol lightweight and allows any builder to use
 * any build system while still integrating with the CRM.
 */
export interface SaveMessage {
  type: `${typeof MESSAGE_PREFIX}:save`
  payload: {
    /** Plugin ID (from deploy result) */
    pluginId?: string
    /** Deployment ID (from deploy result) */
    deploymentId?: string
    /** Instance ID (from deploy result, if auto-instantiated) */
    instanceId?: string
    /** Deployed manifest version (e.g. "1.0.6"), so the host can keep the
     * step's stored instanceVersion in sync with the live deployment. */
    version?: string
    /** Path of the page this save deployed (e.g. "/offer"), so the host can
     * write the step's page path. The deploy result ids alone do not identify
     * a page — a multi-page plugin serves every page from one instance — and a
     * step config with no path fails server validation, which permanently
     * disabled Save on Studio-built steps (TAG-1946). */
    pagePath?: string
    /** Optional: manifest for CRM reference (not for deploy) */
    manifest?: unknown
    /**
     * Source data for round-trip editing.
     * Opaque to the host — stored as-is and passed back via LoadMessage.
     */
    sourceData?: unknown
    /** Correlation ID for matching this request to its response */
    _requestId?: string
  }
}

/**
 * Builder asks for a fresh builder session token before the one it holds
 * expires (#TAG-3478). Host responds with SessionMessage.
 */
export interface RequestSessionMessage {
  type: `${typeof MESSAGE_PREFIX}:request-session`
  payload: {
    /** Correlation ID for matching this request to its response */
    _requestId?: string
  }
}

/**
 * Builder requests a real preview session with actual backend resources.
 * Host responds with PreviewSessionMessage.
 */
export interface RequestPreviewMessage {
  type: `${typeof MESSAGE_PREFIX}:request-preview`
  payload: {
    /** Page path to preview (e.g. /checkout, /offer) */
    pagePath: string
    /** Page type for resource auto-detection */
    pageType?: string
    /** Static resources the builder wants for this preview */
    staticResources?: Record<string, string>
    /** Shipping country for preview (ISO 3166-1 alpha-2, e.g. 'US', 'FR') */
    previewShippingCountry?: string
    /** Display currency override (e.g. 'USD', 'EUR') */
    currency?: string
    /** Display locale override (e.g. 'en', 'fr') */
    locale?: string
    /** Test card preset for payment testing */
    testCardPreset?: 'visa_success' | 'mastercard_success' | 'amex_success' | 'visa_declined' | 'visa_insufficient' | 'visa_3ds'
    /** Builder options for checkout session (e.g. lineItems, orderId) */
    builderOptions?: Record<string, unknown>
    /** Correlation ID for matching this request to its response */
    _requestId?: string
  }
}

/**
 * Builder requests store resources (products, offers, etc.).
 * Host responds with ResourcesMessage.
 */
export interface RequestResourcesMessage {
  type: `${typeof MESSAGE_PREFIX}:request-resources`
  payload: {
    types: Array<'products' | 'offers' | 'shippingRates' | 'orders'>
    /** Correlation ID for matching this request to its response */
    _requestId?: string
  }
}

/**
 * Builder wants to close/exit (return to funnel view).
 */
export interface CloseMessage {
  type: `${typeof MESSAGE_PREFIX}:close`
}

/**
 * Builder requests the host to resize the iframe.
 */
export interface ResizeMessage {
  type: `${typeof MESSAGE_PREFIX}:resize`
  payload: {
    /** Desired height in pixels (width is controlled by host layout) */
    height?: number
    /** Request full-screen mode */
    fullscreen?: boolean
  }
}

/** Builder -> Host: request media library listing */
export interface RequestMediaLibraryMessage {
  type: `${typeof MESSAGE_PREFIX}:request-media-library`
  payload: {
    page?: number
    perPage?: number
    search?: string
    type?: 'image' | 'video' | 'all'
    folder?: string
    /** Correlation ID for matching this request to its response */
    _requestId?: string
  }
}

/** Builder -> Host: register a newly uploaded asset in the media library */
export interface RegisterMediaAssetMessage {
  type: `${typeof MESSAGE_PREFIX}:register-media-asset`
  payload: {
    url: string
    filename: string
    mimeType: string
    fileSize?: number
    width?: number
    height?: number
    folder?: string
    tags?: string[]
    alt?: string
    /** Correlation ID for matching this request to its response */
    _requestId?: string
  }
}

/** Builder -> Host: request navigation to a different funnel step */
/**
 * Builder -> Host: change the STEP's commerce config — what the page sells and
 * who may buy it (offers, order bumps, shipping rates, country rules).
 *
 * The builder cannot write these itself: they live on the funnel node, under
 * keys the canvas owns and rewrites on every compile
 * (`ownedStepConfigKeys`, canvas `lib/canonical.ts`), so a write that went
 * straight to the database would be overwritten by the next canvas compile.
 * The host applies the patch as a canvas command instead, which is also how the
 * builder inherits the canvas's expected-sequence check and its refusals.
 *
 * The patch carries the SAME keys the canvas `set` command accepts. The host
 * drops anything else rather than forwarding it.
 *
 * The host answers with `step-config`, carrying the config as it stands after
 * the write — or, on a refusal, as it still stands, plus the reason.
 */
export interface PatchStepConfigMessage {
  type: `${typeof MESSAGE_PREFIX}:patch-step-config`
  payload: {
    patch: Record<string, unknown>
    /** Correlation ID for matching this request to its `step-config` answer */
    _requestId?: string
  }
}

/**
 * The builder has drawn the view the host's `load` asked for: the page with
 * its content, or the template gallery of a `new-page` load.
 *
 * `ready` only says the builder booted; a host that lifts its loading curtain
 * on `ready` shows the builder's own spinner and empty editor while the
 * document is fetched. A builder that sends this advertises the
 * `render-signal` capability in `ready`, so a host knows it may wait for it.
 */
export interface RenderedMessage {
  type: `${typeof MESSAGE_PREFIX}:rendered`
  payload: {
    view: 'page' | 'gallery'
  }
}

/**
 * The builder could not open the page the host's `load` named: its source is
 * missing, the request failed, or the session was refused. Sent instead of
 * `rendered`, once, so a host waiting on its curtain shows the failure at once
 * rather than after its own timeout. No capability announces it: the message
 * list may only grow, and a host that does not know it drops it and falls back
 * on its own timeout.
 *
 * - `not-found`: there is no source to open for that plugin and deployment
 * - `auth`: the session was refused, after the builder waited for a token
 * - `error`: anything else (network, server error)
 */
export interface LoadFailedMessage {
  type: `${typeof MESSAGE_PREFIX}:load-failed`
  payload: {
    reason: 'not-found' | 'auth' | 'error'
    /** The builder's own wording, for the logs; never shown as is */
    message?: string
  }
}

/**
 * The builder has stored new pictures of a design's page: one desktop and one
 * mobile screenshot, taken from its own render after a save that changed the
 * page, and written on the design (not on a step).
 *
 * Sent after the fact and never answered. A host that shows pictures of the
 * pages it serves re-reads them at once instead of polling for them; a host
 * that does not know the message drops it, like any unhandled action.
 */
export interface PicturesStoredMessage {
  type: `${typeof MESSAGE_PREFIX}:pictures-stored`
  payload: {
    /** The design's deployment the pictures are stored on */
    deploymentId: string
    /** The page of that design they show (e.g. "/checkout") */
    path: string
    /** Public URL of the desktop picture */
    desktop: string
    /** Public URL of the mobile picture */
    mobile: string
    /** When the pictures were taken (ISO 8601) */
    capturedAt: string
  }
}

/**
 * Builder -> Host: the merchant pressed one of the HOST's shortcuts while the
 * focus was inside the builder's frame. Keys pressed in an iframe never reach
 * the page around it, so the builder hands them over by name and the host
 * answers as if pressed on its own page. A host that does not know it drops it.
 *
 * - `assistant`: ⌘J / Ctrl+J, open or close the host's assistant panel
 */
export interface ShortcutMessage {
  type: `${typeof MESSAGE_PREFIX}:shortcut`
  payload: { shortcut: 'assistant' }
}

export interface NavigateStepMessage {
  type: `${typeof MESSAGE_PREFIX}:navigate-step`
  payload: {
    stepId: string
    stepName?: string
  }
}

/** All messages the builder can send to the host */
export type BuilderMessage = 
  | ReadyMessage
  | DirtyMessage
  | SaveMessage
  | RequestPreviewMessage
  | RequestSessionMessage
  | RequestResourcesMessage
  | CloseMessage
  | ResizeMessage
  | RequestMediaLibraryMessage
  | RegisterMediaAssetMessage
  | NavigateStepMessage
  | PatchStepConfigMessage
  | RenderedMessage
  | LoadFailedMessage
  | PicturesStoredMessage
  | ShortcutMessage
  | AgentBuilderMessage

// ─── Unified Message Type ────────────────────────────────────────────

export type ProtocolMessage = HostMessage | BuilderMessage

// ─── Node Editor Registry Type ───────────────────────────────────────

export interface NodeEditorDefinition {
  /** Node type identifier */
  type: NodeEditorType
  /** Display label */
  label: string
  /** Lucide icon name */
  icon: string
  /** How the editor renders */
  editor: 
    | { mode: 'iframe'; url: string; protocol: string }
    | { mode: 'inline'; component: string }
}

// ─── Request Correlation ─────────────────────────────────────────────

/**
 * Optional correlation ID for request/response matching.
 * When present on a request, the host should echo it back on the response
 * so the client can match async responses to their originating requests.
 * This prevents race conditions when multiple overlapping requests are made.
 */
export interface Correlatable {
  _requestId?: string
}

// ─── Helpers ─────────────────────────────────────────────────────────

/** Type guard: is this a protocol message? */
export function isProtocolMessage(data: unknown): data is ProtocolMessage {
  if (typeof data !== 'object' || data === null) return false
  const msg = data as { type?: string }
  return typeof msg.type === 'string' && msg.type.startsWith(`${MESSAGE_PREFIX}:`)
}

/** Extract message type without prefix */
export function getMessageAction(type: string): string {
  return type.replace(`${MESSAGE_PREFIX}:`, '')
}
